
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { tagService } from '@/api/services/tagService';

export interface Tag {
  id: string;
  name: string;
  color?: string;
  entityType?: string;
  count?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TagsState {
  items: Tag[];
  total: number;
  loading: boolean;
  error: string | null;
  currentPage: number;
  itemsPerPage: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  filterEntity: string | null;
  searchQuery: string;
  selectedTags: string[];
}

const initialState: TagsState = {
  items: [],
  total: 0,
  loading: false,
  error: null,
  currentPage: 1,
  itemsPerPage: 20,
  sortField: 'name',
  sortDirection: 'asc',
  filterEntity: null,
  searchQuery: '',
  selectedTags: []
};

// Async thunk for fetching tags
export const fetchTags = createAsyncThunk(
  'tags/fetchTags',
  async (params: {
    searchQuery?: string;
    filterEntity?: string;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    try {
      const response = await tagService.fetchTags(params);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Async thunk for creating a tag
export const createTag = createAsyncThunk(
  'tags/createTag',
  async (tagData: { name: string; color: string }) => {
    try {
      const response = await tagService.createTag(tagData);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Async thunk for updating a tag
export const updateTag = createAsyncThunk(
  'tags/updateTag',
  async ({ id, tag }: { id: string; tag: Partial<Tag> }) => {
    try {
      const response = await tagService.updateTag(id, tag);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Async thunk for deleting tags
export const deleteTags = createAsyncThunk(
  'tags/deleteTags',
  async (tagIds: string[]) => {
    try {
      await tagService.deleteTags(tagIds);
      return tagIds;
    } catch (error) {
      throw error;
    }
  }
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    setTags: (state, action: PayloadAction<Tag[]>) => {
      state.items = action.payload;
      state.total = action.payload.length;
    },
    addTag: (state, action: PayloadAction<Tag>) => {
      state.items.push(action.payload);
      state.total += 1;
    },
    updateTagLocal: (state, action: PayloadAction<{ id: string; updates: Partial<Tag> }>) => {
      const { id, updates } = action.payload;
      const tagIndex = state.items.findIndex(tag => tag.id === id);
      if (tagIndex !== -1) {
        state.items[tagIndex] = { ...state.items[tagIndex], ...updates };
      }
    },
    deleteTag: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(tag => tag.id !== action.payload);
      state.total -= 1;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSort: (state, action: PayloadAction<{ field: string; direction: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setFilter: (state, action: PayloadAction<string | null>) => {
      state.filterEntity = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    selectTag: (state, action: PayloadAction<string>) => {
      const tagId = action.payload;
      if (state.selectedTags.includes(tagId)) {
        state.selectedTags = state.selectedTags.filter(id => id !== tagId);
      } else {
        state.selectedTags.push(tagId);
      }
    },
    selectAllTags: (state, action: PayloadAction<string[]>) => {
      state.selectedTags = action.payload;
    },
    clearTagSelection: (state) => {
      state.selectedTags = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tags
      .addCase(fetchTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        state.total = action.payload.total || 0;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tags';
      })
      // Create tag
      .addCase(createTag.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTag.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.total += 1;
      })
      .addCase(createTag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create tag';
      })
      // Update tag
      .addCase(updateTag.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTag.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTag = action.payload;
        const tagIndex = state.items.findIndex(tag => tag.id === updatedTag.id);
        if (tagIndex !== -1) {
          state.items[tagIndex] = updatedTag;
        }
      })
      .addCase(updateTag.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update tag';
      })
      // Delete tags
      .addCase(deleteTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTags.fulfilled, (state, action) => {
        state.loading = false;
        const deletedIds = action.payload;
        state.items = state.items.filter(tag => !deletedIds.includes(tag.id));
        state.total -= deletedIds.length;
        state.selectedTags = state.selectedTags.filter(id => !deletedIds.includes(id));
      })
      .addCase(deleteTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete tags';
      });
  }
});

export const {
  setTags,
  addTag,
  updateTagLocal,
  deleteTag,
  setLoading,
  setError,
  setPage,
  setSort,
  setFilter,
  setSearchQuery,
  selectTag,
  selectAllTags,
  clearTagSelection
} = tagsSlice.actions;

// Selectors
export const selectTags = (state: RootState) => state.tags.items;
export const selectTagsTotal = (state: RootState) => state.tags.total;
export const selectTagsLoading = (state: RootState) => state.tags.loading;
export const selectTagsError = (state: RootState) => state.tags.error;
export const selectTagsCurrentPage = (state: RootState) => state.tags.currentPage;
export const selectTagsItemsPerPage = (state: RootState) => state.tags.itemsPerPage;
export const selectTagsSortField = (state: RootState) => state.tags.sortField;
export const selectTagsSortDirection = (state: RootState) => state.tags.sortDirection;
export const selectTagsFilterEntity = (state: RootState) => state.tags.filterEntity;
export const selectTagsSearchQuery = (state: RootState) => state.tags.searchQuery;
export const selectSelectedTags = (state: RootState) => state.tags.selectedTags;

export const tagsReducer = tagsSlice.reducer;
export default tagsSlice.reducer;
